import { useState } from 'react';
import { api } from '../api/client.js';
import { clearHumanCycleCache } from '../utils/humanCycles.js';

const THEME_LABELS = {
    sin_estacion: 'Sin estación registrada',
    renovacion: 'Renovación',
    expansion: 'Expansión',
    transicion: 'Transición',
    regeneracion: 'Regeneración',
};

const MONTH_OPTIONS = [
    { v: 1, l: 'Ene' },
    { v: 2, l: 'Feb' },
    { v: 3, l: 'Mar' },
    { v: 4, l: 'Abr' },
    { v: 5, l: 'May' },
    { v: 6, l: 'Jun' },
    { v: 7, l: 'Jul' },
    { v: 8, l: 'Ago' },
    { v: 9, l: 'Sep' },
    { v: 10, l: 'Oct' },
    { v: 11, l: 'Nov' },
    { v: 12, l: 'Dic' },
];

function themeLabel(key) {
    return THEME_LABELS[key] || key;
}

function monthRangeLabel(start, end) {
    const s = MONTH_OPTIONS.find((m) => m.v === Number(start))?.l || start;
    const e = MONTH_OPTIONS.find((m) => m.v === Number(end))?.l || end;
    return `${s} – ${e}`;
}

function buildDraftFromData(data) {
    const cfg = data?.config || data;
    const settings = cfg?.settings || {};
    const seasons = (cfg?.seasons || []).map((s) => ({
        seasonKey: s.seasonKey || s.key,
        label: s.label || '',
        emoji: s.emoji || '',
        tone: s.tone || '',
        accent: s.accent || '#6b9b6e',
        softBg: s.softBg || 'rgba(107, 155, 110, 0.12)',
        gradientSoft: s.gradientSoft || '',
        startMonth: Number(s.startMonth) || 1,
        endMonth: Number(s.endMonth) || 12,
        sortOrder: Number(s.sortOrder) || 0,
        isActive: s.isActive !== false,
        observerHeader: s.observerHeader || '',
        observerSubtitle: s.observerSubtitle || '',
        questionFrames: { ...(s.questionFrames || {}) },
    }));
    return {
        settings: {
            reviewIntervalDays: settings.reviewIntervalDays ?? 90,
            title: settings.title || '',
            lead: settings.lead || '',
            body: settings.body || '',
            durationHint: settings.durationHint || '',
            activeSeasonOverride: settings.activeSeasonOverride || '',
            overrideUntil: settings.overrideUntil
                ? String(settings.overrideUntil).slice(0, 10)
                : '',
        },
        seasons,
    };
}

export default function ProfileEvolutionMastersPanel({
    data,
    loading,
    needsTenant,
    selectedTenantName,
    isSuperAdmin,
    onRefresh,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    if (needsTenant && isSuperAdmin) {
        return (
            <div className="glass-card" style={{ padding: '2rem', textAlign: 'center' }}>
                <h2 style={{ marginTop: 0 }}>Seleccione una empresa</h2>
                <p className="text-muted" style={{ margin: 0, lineHeight: 1.55 }}>
                    Use el selector <strong>Ver por empresa</strong> en la barra superior para ver ciclos y
                    estaciones de esa organización.
                </p>
            </div>
        );
    }

    if (loading && !data) {
        return <p className="text-muted text-center py-lg">Cargando ciclos y estaciones…</p>;
    }

    const config = data?.config;
    const masters = data?.masters || (config ? {
        reviewIntervalDays: config.settings?.reviewIntervalDays,
        calendarCycle: config.calendarCycle,
        currentSeason: config.activeSeason,
        seasons: config.seasons,
        configSource: config.source,
        editableViaUi: { seasons: true, cycleDays: true },
        notes: [],
    } : null);
    const tenantStats = data?.tenantStats;
    const audit = data?.audit || [];

    if (!masters && !config) {
        return <p className="text-muted">No se pudo cargar la configuración de ciclos.</p>;
    }

    const season = masters?.currentSeason || config?.activeSeason;
    const displaySeasons = editing ? draft?.seasons : masters?.seasons || config?.seasons || [];

    function startEdit() {
        setDraft(buildDraftFromData(data));
        setEditing(true);
        setMessage('');
    }

    function cancelEdit() {
        setEditing(false);
        setDraft(null);
        setMessage('');
    }

    function updateSetting(key, value) {
        setDraft((prev) => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    }

    function updateSeason(idx, key, value) {
        setDraft((prev) => {
            const seasons = [...prev.seasons];
            seasons[idx] = { ...seasons[idx], [key]: value };
            return { ...prev, seasons };
        });
    }

    function updateSeasonFrame(idx, order, value) {
        setDraft((prev) => {
            const seasons = [...prev.seasons];
            const frames = { ...seasons[idx].questionFrames, [order]: value };
            seasons[idx] = { ...seasons[idx], questionFrames: frames };
            return { ...prev, seasons };
        });
    }

    async function handleSave() {
        if (!draft) return;
        setSaving(true);
        setMessage('');
        try {
            await api.saveHumanCycleConfig(draft);
            clearHumanCycleCache();
            setEditing(false);
            setDraft(null);
            setMessage('Configuración guardada para esta empresa.');
            onRefresh?.();
        } catch (err) {
            setMessage(err.message || 'No se pudo guardar.');
        } finally {
            setSaving(false);
        }
    }

    async function handleReset() {
        if (!window.confirm('¿Restaurar la configuración global por defecto para esta empresa?')) return;
        setSaving(true);
        setMessage('');
        try {
            await api.resetHumanCycleConfig();
            clearHumanCycleCache();
            setEditing(false);
            setDraft(null);
            setMessage('Configuración restaurada a valores por defecto.');
            onRefresh?.();
        } catch (err) {
            setMessage(err.message || 'No se pudo restaurar.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="profile-evolution-masters stagger">
            <div
                className="glass-card mb-lg"
                style={{ padding: '1.25rem', borderLeft: `4px solid ${season?.accent || 'var(--color-primary)'}` }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2 style={{ margin: '0 0 0.5rem' }}>Ciclos y estaciones humanas</h2>
                        <p className="text-muted mb-0" style={{ lineHeight: 1.55, maxWidth: '52rem' }}>
                            Revisión de ciclo y estaciones emocionales
                            {selectedTenantName ? ` en ${selectedTenantName}` : ''}. Las 10 preguntas núcleo no cambian;
                            solo el marco experiencial.
                        </p>
                        {masters?.configSource && (
                            <p className="text-xs text-muted mt-sm mb-0">
                                Fuente: {masters.configSource === 'tenant' ? 'configuración de empresa' : 'valores por defecto'}
                            </p>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {!editing ? (
                            <button type="button" className="btn btn-primary btn-sm" onClick={startEdit}>
                                Editar configuración
                            </button>
                        ) : (
                            <>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Guardando…' : 'Guardar'}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving}>
                                    Cancelar
                                </button>
                            </>
                        )}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset} disabled={saving}>
                            Restaurar defaults
                        </button>
                    </div>
                </div>
                {message && (
                    <p className="text-sm mt-md mb-0" style={{ color: 'var(--color-primary-dark)' }}>
                        {message}
                    </p>
                )}
            </div>

            <div className="grid-3 mb-lg" style={{ gap: '1rem' }}>
                <div className="glass-card" style={{ padding: '1.25rem' }}>
                    <span className="text-xs text-muted">Ciclo operativo</span>
                    {editing ? (
                        <input
                            type="number"
                            min={30}
                            max={365}
                            className="form-input mt-xs"
                            style={{ fontSize: '1.25rem', fontWeight: 700, width: '100%' }}
                            value={draft?.settings?.reviewIntervalDays ?? 90}
                            onChange={(e) => updateSetting('reviewIntervalDays', Number(e.target.value))}
                        />
                    ) : (
                        <p style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0.35rem 0' }}>
                            {masters.reviewIntervalDays} días
                        </p>
                    )}
                    <p className="text-sm text-muted mb-0">Revisión de manada tras este período</p>
                </div>
                <div className="glass-card" style={{ padding: '1.25rem' }}>
                    <span className="text-xs text-muted">Ciclo calendario</span>
                    <p style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0.35rem 0' }}>{masters.calendarCycle}</p>
                    <p className="text-sm text-muted mb-0">Etiqueta trimestral (comparabilidad)</p>
                </div>
                <div
                    className="glass-card"
                    style={{ padding: '1.25rem', background: season?.softBg, border: `1px solid ${season?.accent}33` }}
                >
                    <span className="text-xs" style={{ color: season?.accent }}>Estación activa hoy</span>
                    <p style={{ fontSize: '1.35rem', fontWeight: 700, margin: '0.35rem 0', color: season?.accent }}>
                        {season?.emoji} {season?.label}
                        {season?.forced ? ' (override)' : ''}
                    </p>
                    <p className="text-sm mb-0" style={{ color: season?.accent }}>{season?.tone}</p>
                </div>
            </div>

            {editing && (
                <div className="glass-card mb-lg" style={{ padding: '1.25rem' }}>
                    <h3 style={{ marginTop: 0 }}>Microcopy — revisión de ciclo</h3>
                    <div className="grid-2" style={{ gap: '0.75rem' }}>
                        <label className="text-sm">
                            Título
                            <input
                                className="form-input w-100 mt-xs"
                                value={draft.settings.title}
                                onChange={(e) => updateSetting('title', e.target.value)}
                            />
                        </label>
                        <label className="text-sm">
                            Duración (hint)
                            <input
                                className="form-input w-100 mt-xs"
                                value={draft.settings.durationHint}
                                onChange={(e) => updateSetting('durationHint', e.target.value)}
                            />
                        </label>
                        <label className="text-sm" style={{ gridColumn: '1 / -1' }}>
                            Frase de apertura
                            <input
                                className="form-input w-100 mt-xs"
                                value={draft.settings.lead}
                                onChange={(e) => updateSetting('lead', e.target.value)}
                            />
                        </label>
                        <label className="text-sm" style={{ gridColumn: '1 / -1' }}>
                            Cuerpo
                            <textarea
                                className="form-input w-100 mt-xs"
                                rows={3}
                                value={draft.settings.body}
                                onChange={(e) => updateSetting('body', e.target.value)}
                            />
                        </label>
                    </div>
                    <h4 className="mt-lg mb-sm">Override de estación (pilotos / campañas)</h4>
                    <div className="grid-2" style={{ gap: '0.75rem' }}>
                        <label className="text-sm">
                            Estación forzada
                            <select
                                className="form-input w-100 mt-xs"
                                value={draft.settings.activeSeasonOverride}
                                onChange={(e) => updateSetting('activeSeasonOverride', e.target.value)}
                            >
                                <option value="">Ninguna (calendario)</option>
                                {draft.seasons.map((s) => (
                                    <option key={s.seasonKey} value={s.seasonKey}>
                                        {s.emoji} {s.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm">
                            Válido hasta (opcional)
                            <input
                                type="date"
                                className="form-input w-100 mt-xs"
                                value={draft.settings.overrideUntil}
                                onChange={(e) => updateSetting('overrideUntil', e.target.value)}
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className="glass-card mb-lg" style={{ padding: '1.25rem' }}>
                <h3 style={{ marginTop: 0 }}>Estaciones humanas</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table className="w-100" style={{ fontSize: '0.9rem', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                <th className="text-left py-sm">Estación</th>
                                <th className="text-left py-sm">Meses</th>
                                <th className="text-left py-sm">Tono</th>
                                <th className="text-left py-sm">Acento</th>
                                <th className="text-left py-sm">Marco (preg. 1)</th>
                                {editing && <th className="text-left py-sm">Activa</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {displaySeasons.map((s, idx) => {
                                const key = s.seasonKey || s.key;
                                const frame = editing
                                    ? draft.seasons[idx]?.questionFrames?.[1]
                                    : s.questionFrames?.[1] || s.questionFrames?.['1'];
                                return (
                                    <tr key={key} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', opacity: s.isActive === false ? 0.5 : 1 }}>
                                        <td className="py-sm">
                                            {editing ? (
                                                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                                    <input
                                                        className="form-input"
                                                        style={{ width: '2.5rem' }}
                                                        value={draft.seasons[idx].emoji}
                                                        onChange={(e) => updateSeason(idx, 'emoji', e.target.value)}
                                                    />
                                                    <input
                                                        className="form-input"
                                                        style={{ minWidth: '7rem' }}
                                                        value={draft.seasons[idx].label}
                                                        onChange={(e) => updateSeason(idx, 'label', e.target.value)}
                                                    />
                                                </div>
                                            ) : (
                                                <span style={{ color: s.accent, fontWeight: 600 }}>
                                                    {s.emoji} {s.label}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-sm text-muted">
                                            {editing ? (
                                                <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                                    <select
                                                        className="form-input"
                                                        value={draft.seasons[idx].startMonth}
                                                        onChange={(e) => updateSeason(idx, 'startMonth', Number(e.target.value))}
                                                    >
                                                        {MONTH_OPTIONS.map((m) => (
                                                            <option key={m.v} value={m.v}>{m.l}</option>
                                                        ))}
                                                    </select>
                                                    <span>–</span>
                                                    <select
                                                        className="form-input"
                                                        value={draft.seasons[idx].endMonth}
                                                        onChange={(e) => updateSeason(idx, 'endMonth', Number(e.target.value))}
                                                    >
                                                        {MONTH_OPTIONS.map((m) => (
                                                            <option key={m.v} value={m.v}>{m.l}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                s.monthRange || monthRangeLabel(s.startMonth, s.endMonth)
                                            )}
                                        </td>
                                        <td className="py-sm">
                                            {editing ? (
                                                <input
                                                    className="form-input w-100"
                                                    value={draft.seasons[idx].tone}
                                                    onChange={(e) => updateSeason(idx, 'tone', e.target.value)}
                                                />
                                            ) : (
                                                s.tone
                                            )}
                                        </td>
                                        <td className="py-sm">
                                            {editing ? (
                                                <input
                                                    type="color"
                                                    value={draft.seasons[idx].accent}
                                                    onChange={(e) => updateSeason(idx, 'accent', e.target.value)}
                                                    title="Acento principal"
                                                />
                                            ) : (
                                                <span style={{ color: s.accent }}>{s.accent}</span>
                                            )}
                                        </td>
                                        <td className="py-sm text-muted" style={{ maxWidth: '22rem' }}>
                                            {editing ? (
                                                <input
                                                    className="form-input w-100"
                                                    value={frame || ''}
                                                    onChange={(e) => updateSeasonFrame(idx, 1, e.target.value)}
                                                />
                                            ) : (
                                                frame || '—'
                                            )}
                                        </td>
                                        {editing && (
                                            <td className="py-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={draft.seasons[idx].isActive !== false}
                                                    onChange={(e) => updateSeason(idx, 'isActive', e.target.checked)}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-muted mt-md mb-0">
                    Edición por empresa · Preguntas globales operario: plantilla oficial ({masters.coreQuestionsCount || 10} preguntas)
                </p>
            </div>

            {audit.length > 0 && (
                <div className="glass-card mb-lg" style={{ padding: '1.25rem' }}>
                    <h4 style={{ marginTop: 0 }}>Historial de cambios</h4>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.85rem' }}>
                        {audit.map((row, i) => (
                            <li key={`${row.created_at}-${i}`} className="mb-xs text-muted">
                                {row.created_at ? new Date(row.created_at).toLocaleString('es') : '—'}
                                {row.editor_name ? ` · ${row.editor_name}` : ''} — {row.summary || row.action}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {tenantStats && (
                <>
                    <div className="grid-4 mb-lg" style={{ gap: '1rem' }}>
                        <div className="glass-card" style={{ padding: '1rem' }}>
                            <span className="text-xs text-muted">Colaboradores activos</span>
                            <strong style={{ fontSize: '1.5rem' }}>{tenantStats.headcount}</strong>
                        </div>
                        <div className="glass-card" style={{ padding: '1rem' }}>
                            <span className="text-xs text-muted">Con perfil ALINEA</span>
                            <strong style={{ fontSize: '1.5rem' }}>{tenantStats.withProfile}</strong>
                        </div>
                        <div className="glass-card" style={{ padding: '1rem' }}>
                            <span className="text-xs text-muted">Revisiones pendientes (≥{masters.reviewIntervalDays}d)</span>
                            <strong style={{ fontSize: '1.5rem' }}>{tenantStats.pendingCycleReview}</strong>
                        </div>
                        <div className="glass-card" style={{ padding: '1rem' }}>
                            <span className="text-xs text-muted">Versiones históricas guardadas</span>
                            <strong style={{ fontSize: '1.5rem' }}>{tenantStats.historicalVersions}</strong>
                        </div>
                    </div>

                    <div className="grid-2 mb-lg" style={{ gap: '1rem' }}>
                        <div className="glass-card" style={{ padding: '1.25rem' }}>
                            <h4 style={{ marginTop: 0 }}>Perfiles por estación guardada</h4>
                            {tenantStats.bySeason?.length ? (
                                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                                    {tenantStats.bySeason.map((row) => (
                                        <li key={row.theme} className="mb-xs">
                                            {themeLabel(row.theme)}: <strong>{row.count}</strong>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted mb-0">Sin datos de estación aún.</p>
                            )}
                        </div>
                        <div className="glass-card" style={{ padding: '1.25rem' }}>
                            <h4 style={{ marginTop: 0 }}>Distribución por versión de perfil</h4>
                            {tenantStats.byVersion?.length ? (
                                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                                    {tenantStats.byVersion.map((row) => (
                                        <li key={row.version} className="mb-xs">
                                            Versión {row.version}: <strong>{row.count}</strong>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted mb-0">Sin perfiles registrados.</p>
                            )}
                        </div>
                    </div>

                    <div className="glass-card" style={{ padding: '1.25rem' }}>
                        <h4 style={{ marginTop: 0 }}>Últimas revisiones de ciclo archivadas</h4>
                        {tenantStats.recentVersions?.length ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="w-100" style={{ fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr>
                                            <th className="text-left">Persona</th>
                                            <th className="text-left">Versión</th>
                                            <th className="text-left">Perfil</th>
                                            <th className="text-left">Ciclo</th>
                                            <th className="text-left">Estación</th>
                                            <th className="text-left">Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tenantStats.recentVersions.map((row, i) => (
                                            <tr key={`${row.full_name}-${row.version_number}-${i}`}>
                                                <td>{row.full_name}</td>
                                                <td>v{row.version_number}</td>
                                                <td>{row.profile_type}</td>
                                                <td>{row.season_cycle || '—'}</td>
                                                <td>{themeLabel(row.season_theme || 'sin_estacion')}</td>
                                                <td>
                                                    {row.created_at
                                                        ? new Date(row.created_at).toLocaleDateString('es')
                                                        : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-muted mb-0">
                                Aún no hay versiones archivadas. Se crean al completar una revisión de ciclo.
                            </p>
                        )}
                    </div>
                </>
            )}

            {masters.notes?.length > 0 && (
                <ul className="text-sm text-muted mt-lg" style={{ lineHeight: 1.55 }}>
                    {masters.notes.map((n) => (
                        <li key={n}>{n}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}
